import { Link } from "react-router-dom";
import { Heart, ShoppingCart, Trash2, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useWishlist } from "@/context/WishlistContext";
import { useCart } from "@/context/CartContext";
import { useLanguage } from "@/context/LanguageContext";
import { useCurrency } from "@/context/CurrencyContext";
import { toast } from "sonner";
import BackButton from "@/components/BackButton";

export const Wishlist = () => {
  const { wishlist, removeFromWishlist, clearWishlist, loading } = useWishlist();
  const { addToCart } = useCart();
  const { t } = useLanguage();
  const { formatPrice } = useCurrency();

  const handleAddToCart = async (product) => {
    const result = await addToCart(product.id, 1);
    if (result.success) {
      toast.success(`${product.name} ${t('addedToWishlist').replace('wishlist', 'cart')}`);
    } else {
      toast.error("Failed to add to cart");
    }
  };

  const handleRemoveFromWishlist = async (productId, productName) => {
    await removeFromWishlist(productId);
    toast.success(`${productName} ${t('removedFromWishlist')}`);
  };

  if (loading) {
    return (
      <div className="pt-24 pb-16">
        <div className="max-w-6xl mx-auto px-4 md:px-8">
          <div className="space-y-6">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="flex gap-6 animate-pulse">
                <div className="w-24 h-24 bg-secondary" />
                <div className="flex-1 space-y-2">
                  <div className="h-4 bg-secondary w-1/2" />
                  <div className="h-4 bg-secondary w-1/4" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (wishlist.length === 0) {
    return (
      <div className="pt-24 pb-16">
        <div className="max-w-6xl mx-auto px-4 md:px-8 text-center">
          <Heart className="w-16 h-16 mx-auto text-muted-foreground mb-4" strokeWidth={1} />
          <h1 className="font-heading text-3xl font-bold mb-2">{t('emptyWishlist')}</h1>
          <p className="text-muted-foreground mb-8">
            {t('saveItemsHint')}
          </p>
          <Link to="/products">
            <Button className="rounded-full uppercase tracking-widest">
              {t('continueShopping')}
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-24 pb-16">
      <div className="max-w-6xl mx-auto px-4 md:px-8">
        {/* Back Button */}
        <div className="mb-4">
          <BackButton />
        </div>
        
        <div className="flex items-center justify-between mb-8">
          <div>
            <p className="text-muted-foreground text-sm uppercase tracking-wider">{t('my')}</p>
            <h1 className="font-heading text-4xl font-bold">{t('wishlist')}</h1>
          </div>
          {wishlist.length > 0 && (
            <Button 
              variant="outline" 
              size="sm"
              onClick={clearWishlist}
              className="text-destructive hover:text-destructive"
              data-testid="clear-wishlist-btn"
            >
              {t('clearAll')}
            </Button>
          )}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {wishlist.map((product) => (
            <div 
              key={product.id} 
              className="group bg-white border border-border rounded-lg overflow-hidden hover:shadow-lg transition-shadow"
              data-testid={`wishlist-item-${product.id}`}
            >
              <Link to={`/product/${product.id}`} className="block">
                <div className="aspect-square bg-gray-100 relative overflow-hidden">
                  {product.image_url ? (
                    <img
                      src={product.image_url}
                      alt={product.name}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-gray-400">
                      {t('noImage')}
                    </div>
                  )}
                </div>
              </Link>
              
              <div className="p-4">
                <Link to={`/product/${product.id}`}>
                  <h3 className="font-medium text-sm mb-1 line-clamp-2 hover:underline">
                    {product.name}
                  </h3>
                </Link>
                <p className="text-lg font-bold price-font mb-4">
                  {formatPrice(product.price)}
                </p>
                
                <div className="flex gap-2">
                  <Button
                    onClick={() => handleAddToCart(product)}
                    className="flex-1 rounded-full text-xs uppercase tracking-wider"
                    size="sm"
                    data-testid={`add-to-cart-${product.id}`}
                  >
                    <ShoppingCart className="h-4 w-4 mr-1" />
                    {t('addToCart')}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleRemoveFromWishlist(product.id, product.name)}
                    className="text-destructive hover:text-destructive hover:bg-destructive/10"
                    data-testid={`remove-wishlist-${product.id}`}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-8 text-center">
          <Link to="/products">
            <Button variant="outline" className="rounded-full uppercase tracking-widest">
              {t('continueShopping')}
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default Wishlist;
